import React from 'react';

/* ── Shared phone chrome ───────────────────────────────────────────── */

export function StatusBar() {
  return (
    <div className="psb">
      <span className="psb-time">9:41</span>
      <div className="psb-icons">
        <div className="psb-dot" /><div className="psb-dot" /><div className="psb-dot" />
      </div>
    </div>
  );
}

const PlayIcon = () => (
  <svg width="7" height="8" viewBox="0 0 7 8" fill="white"><path d="M0 0l7 4-7 4z" /></svg>
);

/* ── Real app-screen iframes ────────────────────────────────────────────────
   The design swaps every phone for an iframe of the actual app screen
   (Profile / Search / Feed), rendered at 390×845 and scaled to the frame width.
   These are the exact screen files from the design bundle, served from
   public/screens/. This mirrors the prototype's runtime swap behaviour. */

export type ScreenName = 'profile' | 'hire' | 'feed';

const SCREEN_SRC: Record<ScreenName, string> = {
  profile: process.env.PUBLIC_URL + '/screens/screen-profile.html?mockup=1',
  hire: process.env.PUBLIC_URL + '/screens/screen-hire.html?mockup=1',
  feed: process.env.PUBLIC_URL + '/screens/screen-feed.html?mockup=1',
};

const SCREEN_W = 390; // intrinsic screen width the app renders at

function PhoneScreen({ screen, frameWidth, screenQuery }: { screen: ScreenName; frameWidth: number; screenQuery?: string }) {
  const scale = (frameWidth / SCREEN_W).toFixed(4);
  const src = screenQuery ? `${SCREEN_SRC[screen]}&${screenQuery}` : SCREEN_SRC[screen];
  return (
    <div className="phone-screen-wrap">
      <iframe
        title={`Tarana ${screen}`}
        src={src}
        className="phone-screen-iframe"
        style={{ transform: `scale(${scale})` }}
        scrolling="no"
        loading="lazy"
      />
    </div>
  );
}

type FrameProps = {
  className?: string;
  style?: React.CSSProperties;
  /** When set, render the real app screen as a scaled iframe instead of `children`. */
  screen?: ScreenName;
  /** Width of the phone frame in px (used to scale the screen). Required with `screen`. */
  frameWidth?: number;
  /** Extra query string appended to the iframe screen URL (e.g. `hideStories=1`). */
  screenQuery?: string;
  children?: React.ReactNode;
};

export function PhoneFrame({ className, style, screen, frameWidth = 236, screenQuery, children }: FrameProps) {
  return (
    <div className={`phone-frame${className ? ' ' + className : ''}`} style={style}>
      {screen ? (
        <PhoneScreen screen={screen} frameWidth={frameWidth} screenQuery={screenQuery} />
      ) : (
        <div className="phone-screen-ui">
          <StatusBar />
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Profile mockup ────────────────────────────────────────────────── */

type Track = { name: string; meta: string; artImg?: string; artGradient?: string };
type ProfileProps = {
  heroImg: string;
  avatarImg: string;
  name: string;
  tag: string;
  bio: string;
  chips: string[];
  stats: { n: string; l: string }[];
  tracks?: Track[];
};

export function ProfileMockup({ heroImg, avatarImg, name, tag, bio, chips, stats, tracks }: ProfileProps) {
  return (
    <div className="pscreen-profile">
      <div className="pscreen-profile-hero">
        <img src={heroImg} alt="" />
        <div className="pscreen-avatar"><img src={avatarImg} alt="" /></div>
      </div>
      <div className="pscreen-pinfo">
        <div className="pscreen-pname">{name}</div>
        <div className="pscreen-ptag">{tag}</div>
        <div className="pscreen-pbio">{bio}</div>
        <div className="pscreen-chips">
          {chips.map((c) => <span className="pscreen-chip" key={c}>{c}</span>)}
        </div>
      </div>
      <div className="pscreen-stats">
        {stats.map((s) => (
          <div className="pscreen-stat" key={s.l}>
            <div className="pscreen-stat-n">{s.n}</div>
            <div className="pscreen-stat-l">{s.l}</div>
          </div>
        ))}
      </div>
      {tracks && (
        <div className="pscreen-tracks">
          {tracks.map((t) => (
            <div className="pscreen-track" key={t.name}>
              <div className="pscreen-track-art" style={t.artGradient ? { background: t.artGradient } : undefined}>
                {t.artImg && <img src={t.artImg} alt="" />}
              </div>
              <div className="pscreen-track-info">
                <div className="pscreen-track-name">{t.name}</div>
                <div className="pscreen-track-meta">{t.meta}</div>
              </div>
              <div className="pscreen-play"><PlayIcon /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Search / discover mockup ──────────────────────────────────────── */

type SearchProps = {
  placeholder: string;
  filters?: { label: string; active?: boolean }[];
  head: string;
  cards: { img: string; name: string; meta: string; tag: string }[];
};

export function SearchMockup({ placeholder, filters, head, cards }: SearchProps) {
  return (
    <div className="pscreen-discover">
      <div className="pscreen-search">
        <span className="pscreen-search-icon">🔍</span>
        <span className="pscreen-search-ph">{placeholder}</span>
      </div>
      {filters && (
        <div className="pm-filter-row">
          {filters.map((f) => (
            <span className={`pm-filter${f.active ? ' active' : ''}`} key={f.label}>{f.label}</span>
          ))}
        </div>
      )}
      <div className="pscreen-dhead">{head}</div>
      <div className="pscreen-cards">
        {cards.map((c) => (
          <div className="pscreen-dcard" key={c.name}>
            <div className="pscreen-dcard-img"><img src={c.img} alt="" /></div>
            <div>
              <div className="pscreen-dcard-name">{c.name}</div>
              <div className="pscreen-dcard-meta">{c.meta}</div>
            </div>
            <div className="pscreen-dcard-tag">{c.tag}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── "Post a Gig" mockup (Home → Organizations preview) ────────────── */

export function PostGigMockup({ applicants }: { applicants: { img: string; name: string; meta: string }[] }) {
  return (
    <div className="pscreen-discover">
      <div className="pm-heading first">Post a Gig</div>
      <div className="pm-block">
        <div className="pm-block-label">Role</div>
        <div className="pm-block-val">Lead Vocalist (Live Tour)</div>
      </div>
      <div className="pm-block">
        <div className="pm-block-label">Budget</div>
        <div className="pm-block-val orange">₹45,000 / show</div>
      </div>
      <div className="pm-heading">Applicants</div>
      <div className="pscreen-cards">
        {applicants.map((a) => (
          <div className="pscreen-dcard" key={a.name}>
            <div className="pscreen-dcard-img"><img src={a.img} alt="" /></div>
            <div>
              <div className="pscreen-dcard-name">{a.name}</div>
              <div className="pscreen-dcard-meta">{a.meta}</div>
            </div>
            <div className="pscreen-dcard-tag">Applied</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Feed mockup ───────────────────────────────────────────────────── */

type FeedProps = {
  avatarImg: string;
  feedImg: string;
  aname: string;
  atag: string;
  primaryLabel: string;
  secondaryLabel: string;
  children?: React.ReactNode; // extra content under the actions row
};

export function FeedMockup({ avatarImg, feedImg, aname, atag, primaryLabel, secondaryLabel, children }: FeedProps) {
  return (
    <div className="pscreen-feed">
      <div className="pscreen-feed-header">
        <span className="pscreen-feed-logo">tarana</span>
        <div className="pm-avatar-sm"><img src={avatarImg} alt="" /></div>
      </div>
      <div className="pscreen-feed-item">
        <img src={feedImg} alt="" />
        <div className="pscreen-feed-info">
          <div className="pscreen-feed-aname">{aname}</div>
          <div className="pscreen-feed-atag">{atag}</div>
        </div>
      </div>
      <div className="pscreen-feed-actions">
        <button className="pscreen-feed-btn pri">{primaryLabel}</button>
        <button className="pscreen-feed-btn sec">{secondaryLabel}</button>
      </div>
      {children}
    </div>
  );
}

/* Trending-tiles block used inside FeedMockup */
export function TrendingTiles({ imgs }: { imgs: string[] }) {
  return (
    <div className="pm-trend-wrap">
      <div className="pm-trend-label">Trending near you</div>
      <div className="pm-trend-row">
        {imgs.map((src) => (
          <div className="pm-trend-tile" key={src}><img src={src} alt="" /></div>
        ))}
        <div className="pm-trend-tile plus"><span>+</span></div>
      </div>
    </div>
  );
}

/* "Now playing" release card used inside the Home → Listeners feed preview */
export function NowPlayingCard({ img, name, meta }: { img: string; name: string; meta: string }) {
  return (
    <div className="pm-feed-pad">
      <div className="pm-feed-now">
        <div className="pm-feed-now-art"><img src={img} alt="" /></div>
        <div>
          <div className="pm-feed-now-name">{name}</div>
          <div className="pm-feed-now-meta">{meta}</div>
        </div>
        <div className="pscreen-play" style={{ marginLeft: 'auto' }}><PlayIcon /></div>
      </div>
    </div>
  );
}
